import { DocumentNode } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import type { LazyQueryHookOptions } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Maybe, PageInfo, ResponsePageInfo, Scalars } from "../gql/graphql";
import { NoInfer } from "react-spring";

export const usePaginatedLoadAll = <
  TData extends {
    edges: Array<TNodeChild>;
    nodes: Array<TNodeChild>;
    pageInfo: ResponsePageInfo;
    totalCount?: Maybe<Scalars["Float"]["output"]>;
  },
  TVariables extends {
    page: Maybe<PageInfo>;
  },
  TNodeChild = any,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: Omit<
    LazyQueryHookOptions<NoInfer<TData>, NoInfer<Omit<TVariables, "page">>>,
    never
  > & {
    variables?: Omit<TVariables, "page">;
    skip?: boolean;
    onResults?: (results: TNodeChild[]) => void;
  },
) => {
  const [loading, setLoading] = useState(!options?.skip);
  const [results, setResults] = useState<TNodeChild[]>([]);
  const [fetchResults] = useLazyQuery(query, options as any);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const fetchAll = useCallback(
    async (variables?: NoInfer<Omit<TVariables, "page">>) => {
      const elemName = (query.definitions[0] as any).selectionSet.selections[0]
        .name.value;
      setLoading(true);
      let hasNextPage: boolean | undefined = true;
      let cursor = null;
      const nodes: TNodeChild[] = [];
      while (hasNextPage) {
        const { data } = await fetchResults({
          variables: {
            ...optionsRef.current?.variables,
            ...variables,
            page: {
              first: 100,
              after: cursor,
            },
          } as TVariables,
        });
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const dataFromElem = (data as any)?.[elemName!];
        nodes.push(...(dataFromElem?.nodes ?? []));
        hasNextPage = dataFromElem?.pageInfo.hasNextPage;
        cursor = dataFromElem?.pageInfo.endCursor;
      }
      setResults(nodes);
      optionsRef.current?.onResults?.(nodes);
      setLoading(false);
      return nodes;
    },
    [query, fetchResults],
  );

  useEffect(() => {
    if (options?.skip) {
      return;
    }
    fetchAll();
  }, [options?.skip, fetchAll]);

  return { loading, results, refetch: fetchAll };
};
